import React from 'react';
import { FileCode, Settings2 } from 'lucide-react';

interface EdiParserConfigProps {
    data: any;
    onChange: (key: string, value: any) => void;
}

export const EdiParserConfig: React.FC<EdiParserConfigProps> = ({ data = {}, onChange }) => {
    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 pb-4 border-b">
                <div className="p-2 bg-indigo-100 rounded-lg">
                    <FileCode className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">EDI Parser Configuration</h3>
                    <p className="text-xs text-gray-500">Configure how the EDI document is parsed</p>
                </div>
            </div>

            <div className="space-y-4">
                {/* Standard Selection */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">EDI Standard</label>
                    <select
                        value={data.standard || 'x12'}
                        onChange={(e) => onChange('standard', e.target.value)}
                        className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                        <option value="x12">ANSI X12</option>
                        <option value="edifact">EDIFACT</option>
                        <option value="custom">Custom</option>
                    </select>
                </div>

                {/* Transaction Set */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Transaction Set</label>
                    <div className="flex gap-2">
                        <select
                            value={data.transactionSet || '835'}
                            onChange={(e) => onChange('transactionSet', e.target.value)}
                            className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        >
                            <option value="835">835 (Health Care Claim Payment/Advice)</option>
                            <option value="837">837 (Health Care Claim)</option>
                            <option value="270">270 (Health Care Eligibility Benefit Inquiry)</option>
                            <option value="271">271 (Health Care Eligibility Response)</option>
                            <option value="277">277 (Health Care Claim Status Response)</option>
                            <option value="834">834 (Benefit Enrollment and Maintenance)</option>
                            <option value="custom">Other / Custom</option>
                        </select>
                    </div>
                </div>

                {/* Advanced Parsing Settings */}
                <div className="pt-4 border-t space-y-4">
                    <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-1">
                        <Settings2 className="w-3 h-3" />
                        Delimiters (Optional)
                    </h4>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700" title="Usually ~">Segment Terminator</label>
                            <input
                                type="text"
                                placeholder="~"
                                maxLength={1}
                                value={data.segmentTerminator || '~'}
                                onChange={(e) => onChange('segmentTerminator', e.target.value)}
                                className="w-full h-8 rounded border border-gray-300 px-2 flex items-center text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-center font-mono bg-gray-50"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700" title="Usually *">Element Separator</label>
                            <input
                                type="text"
                                placeholder="*"
                                maxLength={1}
                                value={data.elementSeparator || '*'}
                                onChange={(e) => onChange('elementSeparator', e.target.value)}
                                className="w-full h-8 rounded border border-gray-300 px-2 flex items-center text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-center font-mono bg-gray-50"
                            />
                        </div>
                    </div>
                    <p className="text-[10px] text-gray-500 text-center">Leave default for standard X12 files.</p>
                </div>
            </div>
        </div>
    );
};
